import { SnackbarProps } from '@mui/material'
import { AlertVariant } from 'ui/components/Alert/Alert'
import { proxy } from 'valtio'
import { recordFiredAlert } from '../../e2e/firedAlertLog'

/**
 * `alertKey` is a stable, semantic identifier for *which* alert fired — independent of the
 * displayed copy and of the active locale. It exists so automated tests can assert on the alert
 * that occurred rather than on its translated text, which is both locale-dependent and changes
 * whenever the copy does.
 *
 * It is optional and purely additive: an alert without one behaves exactly as before. Where the
 * backend already emits a canonical error code (see `elplan.errorAlert.helper.ts`), pass that code —
 * do not invent a second identifier for the same condition.
 */
export type Alert = {
  variant: AlertVariant
  message: string
  props?: Partial<SnackbarProps>
  alertKey?: string
  action?: AlertAction
  note?: string
}

export type AlertAction = {
  label: string
  onClick: () => void
}

export type AlertOptions = {
  action?: AlertAction
  note?: string
}

type AlertSetter = (message: string, props?: Partial<SnackbarProps>, alertKey?: string, options?: AlertOptions) => void

type AlertStore = {
  alert?: Alert
  error: AlertSetter
  success: AlertSetter
  warning: AlertSetter
  info: AlertSetter
  alertClear: () => void
}

function alertSetter(variant: AlertVariant): AlertSetter {
  return (message, props, alertKey, options) => {
    recordFiredAlert({ alertKey, variant, at: Date.now() })
    store.alert = { variant, message, props, alertKey, ...options }
  }
}

const store: AlertStore = proxy<AlertStore>({
  error: alertSetter('error'),
  success: alertSetter('success'),
  warning: alertSetter('warning'),
  info: alertSetter('info'),
  alertClear: () => (store.alert = undefined),
})

export default store
